import { useMemo } from 'react';

interface LocationMapProps {
  address?: string | null;
  city?: string | null;
  /** URL externa de Google Maps si el organizador la pegó a mano; usa esa
   *  para el enlace "Abrir en Maps". El preview embebido igualmente se
   *  calcula desde address+city porque el iframe de Google no acepta
   *  shortlinks. */
  externalUrl?: string | null;
  /** Alto del iframe en px. Default 220. */
  height?: number;
  /** Título opcional encima del mapa. */
  label?: string;
}

/**
 * Preview embebido de Google Maps sin API key (patrón `?q=…&output=embed`).
 * Sin dirección no renderiza nada — así no ensucia formularios vacíos.
 */
export default function LocationMap({
  address,
  city,
  externalUrl,
  height = 220,
  label,
}: LocationMapProps) {
  const query = useMemo(() => {
    const parts = [address, city].filter((v): v is string => Boolean(v && v.trim()));
    return parts.join(', ').trim();
  }, [address, city]);

  if (!query) return null;

  const encoded = encodeURIComponent(query);
  const embedSrc = `https://www.google.com/maps?q=${encoded}&output=embed`;
  const openUrl = externalUrl?.trim() || `https://www.google.com/maps?q=${encoded}`;

  return (
    <div style={{ border: '1px solid #EAE1D2', borderRadius: 10, overflow: 'hidden', background: '#FFF' }}>
      {label && (
        <div
          style={{
            padding: '6px 10px',
            fontSize: 11,
            fontFamily: '"Space Mono", monospace',
            textTransform: 'uppercase',
            letterSpacing: '.5px',
            color: '#6B5F52',
            borderBottom: '1px solid #F5EFE3',
          }}
        >
          {label}
        </div>
      )}
      <iframe
        title={`Mapa · ${query}`}
        src={embedSrc}
        width="100%"
        height={height}
        style={{ border: 0, display: 'block' }}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
      <div
        style={{
          padding: '8px 10px',
          fontSize: 12,
          color: '#6B5F52',
          borderTop: '1px solid #F5EFE3',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          📍 {query}
        </span>
        <a
          href={openUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#1C5D82', fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}
        >
          Abrir en Maps →
        </a>
      </div>
    </div>
  );
}
